import { useMemo } from 'react';
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { DailyPoint, SeriesPoint } from '@/api/dashboard-live';
import { dayLabel, num, time } from './format';
import { usePilotage } from './context';

export interface SeriesDef {
  metric: string;
  label: string;
  color: string;
}

const AXIS = { fontSize: 11 };

function useAxisColors() {
  const { dark } = usePilotage();
  return {
    grid: dark ? '#26324a' : '#eceae4',
    tick: dark ? '#7c8699' : '#8a8984',
    cursor: dark ? '#51607a' : '#c9c7bf',
  };
}

function ChartTooltip({
  active,
  payload,
  label,
  defs,
  format,
  daily,
}: {
  active?: boolean;
  payload?: { dataKey?: string | number; value?: number }[];
  label?: string | number;
  defs: SeriesDef[];
  format: (n: number) => string;
  daily: boolean;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow-lg dark:border-slate-700 dark:bg-slate-800">
      <div className="mb-1 font-medium text-slate-500 dark:text-slate-400">{daily ? dayLabel(String(label), true) : time(String(label))}</div>
      {defs.map((d) => {
        const p = payload.find((x) => x.dataKey === d.metric);
        if (!p || p.value === undefined || p.value === null) return null;
        return (
          <div key={d.metric} className="flex items-center justify-between gap-4">
            <span className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
              <span className="h-2 w-2 rounded-full" style={{ background: d.color }} aria-hidden />
              {d.label}
            </span>
            <span className="font-medium tabular-nums text-slate-900 dark:text-white">{format(Number(p.value))}</span>
          </div>
        );
      })}
    </div>
  );
}

export function Legend({ defs }: { defs: SeriesDef[] }) {
  if (defs.length < 2) return null;
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1">
      {defs.map((d) => (
        <span key={d.metric} className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
          <span className="h-0.5 w-4 rounded" style={{ background: d.color }} aria-hidden />
          {d.label}
        </span>
      ))}
    </div>
  );
}

/** Aligne plusieurs séries sur un axe commun (clé = horodatage ou jour) */
function merge(defs: SeriesDef[], source: Record<string, (SeriesPoint | DailyPoint)[]>): Record<string, number | string>[] {
  const rows = new Map<string, Record<string, number | string>>();
  for (const d of defs) {
    for (const p of source[d.metric] ?? []) {
      const x = 'd' in p ? p.d : p.t;
      const row = rows.get(x) ?? { x };
      row[d.metric] = p.v;
      rows.set(x, row);
    }
  }
  return [...rows.values()].sort((a, b) => String(a.x).localeCompare(String(b.x)));
}

/**
 * Courbes sur la fenêtre fine (5 min). Un seul axe : ne regrouper que des
 * séries de même unité.
 */
export function TimeChart({
  defs,
  height = 160,
  format = num,
  area = false,
  domainMax,
}: {
  defs: SeriesDef[];
  height?: number | string;
  format?: (n: number) => string;
  area?: boolean;
  domainMax?: number;
}) {
  const { series } = usePilotage();
  const c = useAxisColors();
  const data = useMemo(() => merge(defs, series), [defs, series]);

  if (data.length < 2) {
    return <Empty height={height} text="Série en cours de constitution (un point toutes les 5 minutes)" />;
  }

  const common = {
    data,
    margin: { top: 6, right: 8, bottom: 0, left: 0 },
  };
  const axes = (
    <>
      <CartesianGrid stroke={c.grid} vertical={false} />
      <XAxis dataKey="x" tickFormatter={(v) => time(String(v))} tick={{ ...AXIS, fill: c.tick }} axisLine={false} tickLine={false} minTickGap={40} />
      <YAxis tickFormatter={(v) => format(Number(v))} tick={{ ...AXIS, fill: c.tick }} axisLine={false} tickLine={false} width={58} domain={[0, domainMax ?? 'auto']} />
      <Tooltip cursor={{ stroke: c.cursor }} content={<ChartTooltip defs={defs} format={format} daily={false} />} />
    </>
  );

  return (
    <div style={{ height }} onClick={(e) => e.stopPropagation()}>
      <ResponsiveContainer width="100%" height="100%">
        {area ? (
          <AreaChart {...common}>
            {axes}
            {defs.map((d) => (
              <Area key={d.metric} type="monotone" dataKey={d.metric} stroke={d.color} fill={d.color} fillOpacity={0.12} strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
            ))}
          </AreaChart>
        ) : (
          <LineChart {...common}>
            {axes}
            {defs.map((d) => (
              <Line key={d.metric} type="monotone" dataKey={d.metric} stroke={d.color} strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
            ))}
          </LineChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}

/** Historique journalier (valeurs de fin de journée) */
export function DailyChart({
  defs,
  height = 180,
  format = num,
  days,
  bars = false,
  stacked = false,
  domainMax,
  source,
}: {
  defs: SeriesDef[];
  height?: number | string;
  format?: (n: number) => string;
  days?: number;
  bars?: boolean;
  stacked?: boolean;
  domainMax?: number;
  /** historique à utiliser à la place de celui du contexte (plages longues) */
  source?: Record<string, DailyPoint[]>;
}) {
  const { daily: contextDaily } = usePilotage();
  const daily = source ?? contextDaily;
  const c = useAxisColors();
  const data = useMemo(() => {
    const rows = merge(defs, daily);
    return days ? rows.slice(-days) : rows;
  }, [defs, daily, days]);

  if (data.length < 2) {
    return <Empty height={height} text="Historique en cours de constitution (une valeur par jour, à la clôture de minuit)" />;
  }

  const axes = (
    <>
      <CartesianGrid stroke={c.grid} vertical={false} />
      <XAxis dataKey="x" tickFormatter={(v) => dayLabel(String(v))} tick={{ ...AXIS, fill: c.tick }} axisLine={false} tickLine={false} minTickGap={24} />
      <YAxis tickFormatter={(v) => format(Number(v))} tick={{ ...AXIS, fill: c.tick }} axisLine={false} tickLine={false} width={58} domain={[0, domainMax ?? 'auto']} />
      <Tooltip cursor={bars ? { fill: c.grid, opacity: 0.5 } : { stroke: c.cursor }} content={<ChartTooltip defs={defs} format={format} daily />} />
    </>
  );

  return (
    <div style={{ height }} onClick={(e) => e.stopPropagation()}>
      <ResponsiveContainer width="100%" height="100%">
        {bars ? (
          <BarChart data={data} margin={{ top: 6, right: 8, bottom: 0, left: 0 }} barCategoryGap={2}>
            {axes}
            {defs.map((d, i) => (
              <Bar
                key={d.metric}
                dataKey={d.metric}
                fill={d.color}
                stackId={stacked ? 's' : undefined}
                radius={stacked ? (i === defs.length - 1 ? [4, 4, 0, 0] : 0) : [4, 4, 0, 0]}
                stroke={stacked ? 'var(--pilotage-surface)' : undefined}
                strokeWidth={stacked ? 1 : 0}
                isAnimationActive={false}
              />
            ))}
          </BarChart>
        ) : (
          <LineChart data={data} margin={{ top: 6, right: 8, bottom: 0, left: 0 }}>
            {axes}
            {defs.map((d) => (
              <Line key={d.metric} type="monotone" dataKey={d.metric} stroke={d.color} strokeWidth={2} dot={data.length < 20 ? { r: 3, strokeWidth: 0, fill: d.color } : false} isAnimationActive={false} connectNulls />
            ))}
          </LineChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}

/** Histogramme horaire (24 barres) */
export function HourlyBars({ values, color, format = num, height = 90 }: { values: number[]; color: string; format?: (n: number) => string; height?: number }) {
  const c = useAxisColors();
  const data = values.map((v, h) => ({ x: `${h} h`, v }));
  return (
    <div style={{ height }} onClick={(e) => e.stopPropagation()}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 4, right: 0, bottom: 0, left: 0 }} barCategoryGap={2}>
          <XAxis dataKey="x" tick={{ ...AXIS, fill: c.tick }} axisLine={false} tickLine={false} interval={3} />
          <Tooltip
            cursor={{ fill: c.grid, opacity: 0.5 }}
            content={({ active, payload }) =>
              active && payload?.length ? (
                <div className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs shadow dark:border-slate-700 dark:bg-slate-800">
                  <span className="text-slate-400">{String(payload[0].payload.x)} · </span>
                  <span className="font-medium tabular-nums text-slate-900 dark:text-white">{format(Number(payload[0].value))}</span>
                </div>
              ) : null
            }
          />
          <Bar dataKey="v" fill={color} radius={[3, 3, 0, 0]} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function Empty({ height, text }: { height: number | string; text: string }) {
  return (
    <div style={{ height }} className="flex items-center justify-center rounded-lg border border-dashed border-slate-200 px-4 text-center text-xs text-slate-400 dark:border-slate-800">
      {text}
    </div>
  );
}
